import * as React from 'react'
import { DayPicker, type DayButtonProps } from 'react-day-picker'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ar, enGB, fr } from 'react-day-picker/locale'
import { cn } from '../../lib/utils'
import { normalizeAppLanguage } from 'helpers/documentLocale'
import { buttonVariants } from './button'

export type CalendarProps = React.ComponentProps<typeof DayPicker>

function resolveDayPickerLocale(language: string) {
 const lang = normalizeAppLanguage(language)
 if (lang === 'fr') return fr
 if (lang === 'en') return enGB
 return ar
}

function CalendarDayButton({ day, modifiers, className, ...props }: DayButtonProps) {
 const ref = React.useRef<HTMLButtonElement>(null)
 const isOutsideDuplicate =
  modifiers.outside &&
  (modifiers.range_start ||
   modifiers.range_end ||
   modifiers.range_middle ||
   modifiers.selected)
 const isSingleSelected =
  modifiers.selected &&
  !modifiers.range_start &&
  !modifiers.range_end &&
  !modifiers.range_middle
 const showRangeStart = modifiers.range_start && !modifiers.outside
 const showRangeEnd = modifiers.range_end && !modifiers.outside
 const showRangeMiddle = modifiers.range_middle && !modifiers.outside
 const showTodayMarker =
  modifiers.today &&
  !modifiers.selected &&
  !modifiers.range_start &&
  !modifiers.range_end &&
  !modifiers.range_middle

 React.useEffect(() => {
  if (modifiers.focused) ref.current?.focus()
 }, [modifiers.focused])

 if (isOutsideDuplicate) {
  return (
   <button
    ref={ref}
    type="button"
    tabIndex={-1}
    aria-hidden
    disabled
    className={cn(
     buttonVariants({ variant: 'ghost', size: 'icon-sm' }),
     'h-10 w-10 cursor-default p-0 font-normal text-muted-foreground opacity-35',
     className
    )}
    {...props}
   >
    {props.children}
   </button>
  )
 }

 return (
  <button
   ref={ref}
   type="button"
   data-day={day.date.toLocaleDateString()}
   data-selected-single={isSingleSelected || undefined}
   data-range-start={showRangeStart || undefined}
   data-range-end={showRangeEnd || undefined}
   data-range-middle={showRangeMiddle || undefined}
   className={cn(
    buttonVariants({ variant: 'ghost', size: 'icon-sm' }),
    'relative h-10 w-10 p-0 font-medium text-foreground',
    'data-[selected-single=true]:bg-[hsl(var(--primary))]',
    'data-[selected-single=true]:font-semibold data-[selected-single=true]:text-primary-foreground',
    'data-[selected-single=true]:hover:bg-[hsl(var(--primary))]',
    'data-[selected-single=true]:hover:text-primary-foreground',
    'data-[range-start=true]:rounded-s-xl data-[range-start=true]:bg-[hsl(var(--primary))]',
    'data-[range-start=true]:font-semibold data-[range-start=true]:text-primary-foreground',
    'data-[range-start=true]:hover:bg-[hsl(var(--primary))]',
    'data-[range-start=true]:hover:text-primary-foreground',
    'data-[range-end=true]:rounded-e-xl data-[range-end=true]:bg-[hsl(var(--primary))]',
    'data-[range-end=true]:font-semibold data-[range-end=true]:text-primary-foreground',
    'data-[range-end=true]:hover:bg-[hsl(var(--primary))]',
    'data-[range-end=true]:hover:text-primary-foreground',
    'data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-[hsl(var(--primary-soft-surface))]/90',
    'data-[range-middle=true]:text-foreground',
    'data-[range-middle=true]:hover:bg-[hsl(var(--primary-soft-surface))]/90',
    'data-[range-middle=true]:hover:text-foreground',
    showTodayMarker &&
     'font-semibold text-primary after:absolute after:bottom-1 after:start-1/2 after:h-1 after:w-1 after:-translate-x-1/2 after:rounded-full after:bg-primary/80',
    className
   )}
   {...props}
  />
 )
}

function Calendar({
 className,
 classNames,
 showOutsideDays = true,
 numberOfMonths,
 hideNavigation = false,
 pagedNavigation,
 ...props
}: CalendarProps) {
 const { i18n } = useTranslation()
 const locale = resolveDayPickerLocale(i18n.language)
 const rtl = locale === ar
 const monthsCount = numberOfMonths ?? 1
 const usePagedNavigation = pagedNavigation ?? monthsCount > 1
 const isRangeMode = props.mode === 'range'

 return (
  <DayPicker
   showOutsideDays={showOutsideDays}
   locale={locale}
   dir={rtl ? 'rtl' : 'ltr'}
   numberOfMonths={monthsCount}
   hideNavigation={hideNavigation}
   pagedNavigation={usePagedNavigation}
   className={cn('p-1', className)}
   classNames={{
    months: 'flex flex-col gap-4 sm:flex-row sm:gap-5',
    month: 'space-y-2',
    month_caption: 'relative flex items-center justify-center px-2 pb-1',
    caption_label: 'text-base font-bold text-foreground text-center',
    nav: 'hidden',
    button_previous: 'hidden',
    button_next: 'hidden',
    month_grid: 'w-full border-collapse',
    weekdays: 'flex',
    weekday: 'w-10 text-center text-[0.75rem] font-semibold text-[hsl(var(--primary))]',
    week: 'mt-0.5 flex w-full',
    day: cn(
     'relative h-10 w-10 p-0 text-center text-sm',
     isRangeMode &&
      '[&:not(.rdp-outside):has([data-range-middle=true])]:bg-[hsl(var(--primary-soft-surface))]/90 [&:not(.rdp-outside):has([data-range-start=true])]:bg-[hsl(var(--primary-soft-surface))]/90 [&:not(.rdp-outside):has([data-range-end=true])]:bg-[hsl(var(--primary-soft-surface))]/90'
    ),
    day_button: 'h-10 w-10',
    selected: 'rdp-selected',
    today: 'font-bold',
    outside: 'text-muted-foreground opacity-45',
    disabled: 'text-muted-foreground opacity-35',
    range_middle: 'rounded-none',
    range_start: 'rounded-s-xl',
    range_end: 'rounded-e-xl',
    hidden: 'invisible',
    ...classNames,
   }}
   components={{
    DayButton: CalendarDayButton,
    Chevron: ({ orientation, className: chevronClassName, ...chevronProps }) => {
     const Icon =
      rtl
       ? orientation === 'left'
         ? ChevronRight
         : ChevronLeft
       : orientation === 'left'
         ? ChevronLeft
         : ChevronRight
     return (
      <Icon className={cn('h-4 w-4 text-[hsl(var(--primary))]', chevronClassName)} {...chevronProps} />
     )
    },
   }}
   {...props}
  />
 )
}

Calendar.displayName = 'Calendar'

export { Calendar }
